import { ISystemResponse } from '@kcftech/prometheustypescriptapiclient';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownProps,
} from 'components/dropdown/Dropdown';
import { sdClient } from 'contexts/SDKClientContext';
import { useInfiniteDataSource } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { useCallback, useEffect } from 'react';
import { getId } from 'utils/data';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

const renderLabel = (value?: ISystemResponse, color?: string) => (
    <div style={{ color }}>{value?.Name ?? 'Select a System'}</div>
);

export type SystemDropdownDrops = Readonly<{
    /**
     * The corporation ID to fetch systems from.
     */
    corporationId?: string;
    /**
     * The location ID to fetch systems from.
     */
    locationId?: string;
    /**
     * The currently selected system.
     */
    system?: ISystemResponse;
    /**
     * Optional color for the label text.
     */
    labelTextColor?: string;
    /**
     * Callback that is called when a new system is selected.
     */
    onChange: (value: ISystemResponse) => void;
    /**
     * Callback when we select an option from the dropdown
     */
    onDropdownSelect?: () => void;
}> &
    Pick<DropdownProps<ISystemResponse>, 'trigger' | 'popup'>;

const getFilter = (locationId: string | undefined, corporationId: string | undefined) => {
    if (locationId) {
        return Filter.eq('LocationId', locationId);
    }
    if (corporationId) {
        return Filter.eq('CorporationId', corporationId);
    }
    return undefined;
};

const SystemDropdown = ({
    corporationId,
    locationId,
    labelTextColor,
    trigger,
    popup,
    onChange,
    onDropdownSelect,
    system,
}: SystemDropdownDrops) => {
    const {
        isLoading,
        hasNext,
        loadNext,
        results,
        filter,
        setFilter,
    } = useInfiniteDataSource({
        name: 'system-dropdown',
        defaultSort: [{ field: 'Name', dir: 'asc' }],
        defaultFilter: { name: '' },
        pageLimit: 20,
        query: ({ sort, page, pageLimit, filter: filterValue }) => {
            const sortExpr = buildOrderBy(sort);

            const filterExpr = getFilter(locationId, corporationId);
            return toDataSourceResult(
                sdClient.getSystems({
                    filter: filterExpr,
                    page,
                    pageLimit,
                    orderBy: sortExpr,
                    nameSearch: filterValue.name,
                })
            );
        },
    });

    useEffect(() => setFilter((filterValue) => ({ ...filterValue, locationId })), [
        locationId,
        setFilter,
    ]);

    // Function for changing the name filter state easily
    const setSearchText = useCallback(
        (searchText: string) => {
            setFilter((filterValue) => ({
                ...filterValue,
                name: searchText,
            }));
        },
        [setFilter]
    );

    const renderItem: DropdownItemRenderer<ISystemResponse> = ({ item }) => {
        if (locationId === undefined) {
            // if we do not have a location in the breadcrumb, show it in the path for the marker form
            return `${item.Location.LocationName} / ${item.Name}`;
        }
        return item.Name;
    };

    return (
        <Dropdown<ISystemResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            value={system}
            renderItem={renderItem}
            renderLabel={({ value }) => renderLabel(value, labelTextColor)}
            onChange={(newValue) => {
                onDropdownSelect?.();
                onChange(newValue);
            }}
            filterable={{
                filterText: filter.name,
                onChange: setSearchText,
                placeholder: 'Search Systems',
            }}
            trigger={trigger}
            popup={{ width: '30rem', ...popup }}
        />
    );
};

export { SystemDropdown };
